import { Button, Flex, Input, message, Space, Tag, Typography } from 'antd'
import React, { ReactNode, useMemo } from 'react'
import { DesignSystem } from '~/core/designSystem'
import { Toolbox } from '~/plugins/toolbox/shared'

type LabelValue = {
  name: string
}

type Label = {
  label: ReactNode
  default?: boolean
  value: LabelValue
}

type Props = {
  id?: string
  value?: LabelValue[]
  initialValues?: LabelValue[]
  suggestions?: Label[]
  onChange?: (values: LabelValue[]) => void
}

export const LabelPicker: React.FC<Props> = ({
  id,
  value: values = [],
  suggestions,
  onChange,
}) => {
  const [customLabel, setCustomLabel] = React.useState('')

  const designSystem = DesignSystem.use()

  const suggestionsAvailable = suggestions?.filter(
    suggestion =>
      !values.some(val => val.name === suggestion.value.name) &&
      !suggestion.default,
  )

  const suggestionsVisible = useMemo(() => {
    const hasSuggestions = suggestionsAvailable?.length > 0
    if (!hasSuggestions) {
      return []
    }

    return Toolbox.Utility.pickRandomSet(suggestionsAvailable, 5)
  }, [suggestionsAvailable])

  const toggleTag = (tag: LabelValue) => {
    const isSelected = values.some(val => val.name === tag.name)
    const newValues = isSelected
      ? values.filter(val => val.name !== tag.name)
      : [...values, tag]

    onChange?.(newValues)
  }

  return (
    <Flex gap={16} id={id} wrap={!designSystem.isMobile} vertical>
      <Flex gap={8} wrap>
        {values.map(label => (
          <Tag
            key={label.name}
            closable
            onClose={() => toggleTag(label)}
            style={{
              fontSize: 14,
              padding: '5px 10px',
              background: DesignSystem.ThemeConst.PRIMARY_YELLOW,
              borderColor: DesignSystem.ThemeConst.PRIMARY_YELLOW,
            }}
          >
            {label.name}
          </Tag>
        ))}

        <Input
          value={customLabel}
          placeholder={
            values.length > 0
              ? 'Add label'
              : 'Choose a suggested label or type your own'
          }
          size="small"
          maxLength={50}
          bordered={false}
          style={{
            width: values.length > 0 ? 100 : undefined,
            paddingLeft: 4,
            paddingRight: 4,
            background: 'transparent',
            boxShadow: 'none',
          }}
          onChange={e => setCustomLabel(e.target.value)}
          onPressEnter={e => {
            e.preventDefault()
            const trimmed = customLabel.trim()
            if (!trimmed) return

            const normalized = trimmed.toLowerCase()
            if (values.some(v => v.name.toLowerCase() === normalized)) {
              message.warning('Label already added')
            } else {
              onChange?.([...values, { name: trimmed }])
            }

            setCustomLabel('')
          }}
          onBlur={() => {
            const trimmed = customLabel.trim()
            if (!trimmed) return

            const normalized = trimmed.toLowerCase()
            if (values.some(v => v.name.toLowerCase() === normalized)) {
              message.warning('Label already added')
            } else {
              onChange?.([...values, { name: trimmed }])
            }

            setCustomLabel('')
          }}
        />
      </Flex>
      {suggestionsVisible?.length > 0 && (
        <>
          <Typography.Text type="secondary">Ideas</Typography.Text>
          <Space wrap>
            {suggestionsVisible?.map((suggestion, index) => (
              <Button
                key={index}
                size="small"
                variant="filled"
                color="default"
                style={{ color: 'grey', fontSize: '12px' }}
                onClick={() => toggleTag(suggestion?.value)}
              >
                ✨ {suggestion?.label}
              </Button>
            ))}
          </Space>
        </>
      )}
    </Flex>
  )
}
